import { DesignerAttribution } from '@vp/ep-designer-attribution';
import {
    BasicResponsiveImage,
    StandardTile,
    StandardTileContents,
    StandardTileImage,
    StandardTileName,
    StandardTileOverlay,
    tokens,
} from '@vp/swan';
import { StaticImageData } from 'next/image';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import {
    APP_LOCALE,
    BIFROST_ENABLED_LOCALES,
    useIdentityContext,
} from '@99designs/design-services-common';
import { linkTo } from '@99designs/routing';
import { usePage } from '@99designs/tracking';
import { sendNavigationEvent } from '@99designs/tracking';
import vistaAvatar from '../../images/vista-avatar.png';
import { useTrackingForProduct, useUIConfigForProduct } from '../../products';
import type { DesignServicesProductNames } from '../../types';

interface ProductTileProps {
    title: string;
    productKey: DesignServicesProductNames;
    displayIndex: number;
}

const AttributionWrapper = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 0 ${tokens.SwanSemSpace4} ${tokens.SwanSemSpace4} ${tokens.SwanSemSpace4};
    z-index: 1;
    width: inherit;
`;

// !important required as SWAN default is aligning items to the center
const StyledStandardTileOverlay = styled(StandardTileOverlay)`
    height: 100%;
    align-items: flex-start !important;
`;

export function ProductTile({ title, productKey, displayIndex }: ProductTileProps) {
    const { pageData } = usePage();
    const { locale = APP_LOCALE, query } = useRouter();
    const bifrostFlagEnabled = BIFROST_ENABLED_LOCALES.includes(locale);
    const isBifrostEnabled = bifrostFlagEnabled || query['bifrostEnabled'] === 'true';

    const { image, ctaLink, isAvailableInLocale } = useUIConfigForProduct(
        productKey,
        locale,
        isBifrostEnabled
    );

    const { anonymousUserId } = useIdentityContext();
    const { crossPlatformTracking, label, eventDetail, navigationDetail } = useTrackingForProduct(
        productKey,
        locale,
        anonymousUserId,
        isBifrostEnabled
    );
    const productLink = crossPlatformTracking ? ctaLink + crossPlatformTracking : ctaLink;

    if (!isAvailableInLocale) return null;

    return (
        <StandardTile
            skin="standard"
            imageWidth="full-bleed"
            render={({ className, children }) => (
                <a
                    href={productLink}
                    onClick={() =>
                        sendNavigationEvent({
                            label,
                            eventDetail,
                            navigationDetail,
                            ...pageData,
                        })
                    }
                    data-section={label}
                    data-position={displayIndex}
                    data-translation={navigationDetail}
                    data-testid="product-tile"
                    className={className}
                >
                    {children}
                </a>
            )}
        >
            <StandardTileImage>
                {image.designer && (
                    <StyledStandardTileOverlay>
                        <AttributionWrapper>
                            <DesignerAttribution
                                designerName={image.designer.displayName}
                                avatarURL={
                                    image.designer.userId === 'vistaprint'
                                        ? (vistaAvatar as unknown as StaticImageData).src
                                        : linkTo.attributionAvatar(image.designer.userId, 128)
                                }
                                darkMode={image.isDarkImage}
                            />
                        </AttributionWrapper>
                    </StyledStandardTileOverlay>
                )}
                <BasicResponsiveImage aspectRatio={1} as="picture">
                    <img src={(image.url as StaticImageData).src} alt="" role="presentation" />
                </BasicResponsiveImage>
            </StandardTileImage>
            <StandardTileContents>
                <StandardTileName>{title}</StandardTileName>
            </StandardTileContents>
        </StandardTile>
    );
}
